import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import '../styles/tokens.scss';
import './styles.scss';

export interface IUptimeDropdownProps {
    label: ReactNode;
    children: ReactNode | ((close: () => void) => ReactNode);
    badge?: number;
    align?: 'left' | 'right';
    disabled?: boolean;
    className?: string;
    triggerClassName?: string;
    menuClassName?: string;
}

export const UptimeDropdown = ({
    label,
    children,
    badge,
    align = 'left',
    disabled,
    className,
    triggerClassName,
    menuClassName,
}: IUptimeDropdownProps) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const menuId = useId();
    const close = () => setOpen(false);

    useEffect(() => {
        if (!open) {
            return;
        }
        const handleClick = (event: MouseEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [open]);

    return (
        <div ref={rootRef} className={`upt-dropdown${className ? ` ${className}` : ''}`}>
            <button
                type='button'
                className={triggerClassName ?? 'upt-dropdown__trigger'}
                aria-expanded={open}
                aria-controls={menuId}
                disabled={disabled}
                onClick={() => setOpen(value => !value)}
            >
                {label}
                {!!badge && <span className='upt-dropdown__badge'>{badge}</span>}
                <span className='upt-dropdown__caret' aria-hidden='true'>
                    ▾
                </span>
            </button>
            {open && (
                <div
                    id={menuId}
                    className={`upt-dropdown__menu upt-dropdown__menu--${align}${menuClassName ? ` ${menuClassName}` : ''}`}
                >
                    {typeof children === 'function' ? children(close) : children}
                </div>
            )}
        </div>
    );
};

export interface IUptimeDropdownCheckItemProps {
    label: ReactNode;
    checked: boolean;
    onChange: (checked: boolean) => void;
    meta?: ReactNode;
}

export const UptimeDropdownCheckItem = ({ label, checked, onChange, meta }: IUptimeDropdownCheckItemProps) => (
    <label className='upt-dropdown__item'>
        <input type='checkbox' checked={checked} onChange={event => onChange(event.target.checked)} />
        <span className='upt-dropdown__item-label'>{label}</span>
        {meta !== undefined && <span className='upt-dropdown__item-meta'>{meta}</span>}
    </label>
);

export default UptimeDropdown;
